import * as Sentry from '@sentry/react';

import { isE2E, isHeadless, isProdEnv } from './utils/env';

export type SentryOrganization = {
  id: string;
  name: string;
};

export type InitSentryOptions = {
  getOrganization?: () => SentryOrganization | null | undefined;
  isProd?: boolean;
};

const beforeBreadcrumb = (
  breadcrumb: Sentry.Breadcrumb,
  hint: Sentry.BreadcrumbHint | undefined
) => {
  if (breadcrumb.category === 'ui.click' && hint?.event?.target) {
    const target = hint.event.target as HTMLElement;

    const identifiers: Record<
      'ariaLabel' | 'testId' | 'placeholder',
      string | undefined
    > = {
      ariaLabel: target.ariaLabel ?? undefined,
      testId: target.dataset.testid ?? undefined,
      placeholder: target.getAttribute('placeholder') ?? undefined,
    };

    if (Object.values(identifiers).some((it) => !!it)) {
      Sentry.addBreadcrumb({
        type: 'debug',
        category: 'ui.clicked.element',
        level: 'info',
        data: identifiers,
      });
    }

    if (identifiers.ariaLabel) {
      breadcrumb.message = identifiers.ariaLabel;
    }
  }

  return breadcrumb;
};

export const initSentry = ({
  getOrganization,
  isProd = isProdEnv(),
}: InitSentryOptions = {}) => {
  const beforeSendSpan: Sentry.BrowserOptions['beforeSendSpan'] = (span) => {
    const org = getOrganization?.();

    if (org) {
      span.data ??= {};
      span.data['organization.id'] = org.id;
      span.data['organization.name'] = org.name;
    }

    return span;
  };

  Sentry.init({
    enabled: !isE2E() && !isHeadless() && isProd,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration(),
      Sentry.extraErrorDataIntegration(),
      Sentry.httpClientIntegration({
        failedRequestStatusCodes: [
          [400, 400],
          [402, 499],
          [500, 599],
        ],
      }),
    ],
    debug: window.env.VITE_SENTRY_DEBUG === 'true',
    dsn: window.env.VITE_SENTRY_CLIENT_DSN,
    release: window.env.VITE_VERSION,
    environment: window.env.VITE_ENV,
    normalizeDepth: 10,
    beforeBreadcrumb,
    beforeSendSpan,
    tracesSampler: (samplingContext) => {
      if (samplingContext.attributes?.['sentry.origin']?.includes('pageload')) {
        return 0.5;
      }

      return 0.1;
    },
    profilesSampleRate: 0.1,
  });
};
